import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";

// 📌 FETCH Pending Stock Transfer LIST (with pagination + filters)
export const fetchPendingStockTransferList = createAsyncThunk(
  "pst/fetchList",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
    warehouse_id,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    warehouse_id?: string;
  }) => {
    console.log("FILTER VALUE RECEIVED BY API CALL:", filters);
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());

    if (warehouse_id) params.append("warehouse_id", warehouse_id);
    if (search) params.append("search", search);

    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    // Helper
    const formatDate = (date: any) => {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0]; // ALWAYS YYYY-MM-DD, no timezone issues
    };

    // Apply filters
    if (filters?.length) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue === undefined || filterValue === null)
            return;

          // 🗓 Handle date-range filters (object with from_date / to_date)
          if (typeof filterValue === "object" && filterValue !== null) {
            const { from_date, to_date } = filterValue;

            // Created / Updated / Dispatch Date → send field-specific parameters
            if (filterField === "dispatch_date") {
              if (from_date) {
                params.append(`dispatch_date_from`, formatDate(from_date));
              }
              if (to_date) {
                params.append(`dispatch_date_to`, formatDate(to_date));
              }
            } else if (filterField === "created_at") {
              if (from_date) {
                params.append(`created_at_from`, formatDate(from_date));
              }
              if (to_date) {
                params.append(`created_at_to`, formatDate(to_date));
              }
            } else if (filterField === "updated_at") {
              if (from_date) {
                params.append(`updated_at_from`, formatDate(from_date));
              }
              if (to_date) {
                params.append(`updated_at_to`, formatDate(to_date));
              }
            }

            return; // Stop processing here
          }

          // 🔤 Simple filters (string, number)
          params.append(filterField, String(filterValue));
        }
      );
    }

    // API CALL
    const url = `${api.warehouse2.pendingStockTransfer.list}?${params.toString()}`;
    
    const response: any = await Axios.get(url);

    return {
      data: response?.data?.data || [],
      rowCount: response?.data?.count || 0,
    };
  }
);

// 📌 DETAILS FETCH
export const fetchPendingStockTransferDetails = createAsyncThunk(
  "pst/fetchDetails",
  async (transfer_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.get(
        `${api.warehouse2.pendingStockTransfer.details}${transfer_id}`
      );
      return res?.data?.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed fetching pending stock transfer details"
      );
    }
  }
);

// 📌 CREATE
export const createPendingStockTransfer = createAsyncThunk(
  "pst/create",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res: any = await Axios.post(
        api.warehouse2.pendingStockTransfer.create,
        payload
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed creating pending stock transfer"
      );
    }
  }
);

// 📌 UPDATE
export const updatePendingStockTransfer = createAsyncThunk(
  "pst/update",
  async ({ transfer_id, payload }: { transfer_id: string; payload: any }, { rejectWithValue }) => {
    try {
      const res: any = await Axios.put(
        `${api.warehouse2.pendingStockTransfer.update}${transfer_id}/`,
        payload
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed updating pending stock transfer"
      );
    }
  }
);

// 📌 DELETE
export const deletePendingStockTransfer = createAsyncThunk(
  "pst/delete",
  async (transfer_id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(
        `${api.warehouse2.pendingStockTransfer.delete}${transfer_id}/`
      );
      return transfer_id;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed deleting record"
      );
    }
  }
);

// 📌 EXPORT EXCEL
export const exportPendingStockTransferExcel = async ({
  search,
  filters,
  warehouse_id,
}: {
  search?: string;
  filters?: any;
  warehouse_id: string;
}) => {
  const params = new URLSearchParams();
  params.append("export", "excel");
  params.append("warehouse_id", warehouse_id);

  if (search) params.append("search", search);

  filters?.forEach(({ filterField, filterValue }: any) => {
    if (filterValue) params.append(filterField, filterValue);
  });

  const res = await Axios.get(
    `${api.warehouse2.pendingStockTransfer.list}?${params.toString()}`,
    { responseType: "blob" }
  );

  return {
    blob: res.data,
    filename:
      res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") ||
      "PendingStockTransfer.xlsx",
  };
};

// 📌 EXPORT CSV
export const exportPendingStockTransferCSV = async ({
  sort,
  order,
  search,
  filters,
  warehouse_id,
}: {
  sort?: string;
  order?: "asc" | "desc" | "";
  search?: string;
  filters?: any;
  warehouse_id?: string;
}) => {
  const params = new URLSearchParams();
  params.append("export", "csv");
  
  if (warehouse_id) params.append("warehouse_id", warehouse_id);
  if (search) params.append("search", search);
  
  if (sort) {
    const orderingValue = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingValue);
  }

  // Helper
  const formatDate = (date: any) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  if (filters?.length) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (!filterField || filterValue === undefined || filterValue === null)
          return;

        // 🗓 Handle date-range filters (object with from_date / to_date)
        if (typeof filterValue === "object" && filterValue !== null) {
          const { from_date, to_date } = filterValue;

          if (filterField === "dispatch_date") {
            if (from_date) {
              params.append(`dispatch_date_from`, formatDate(from_date));
            }
            if (to_date) {
              params.append(`dispatch_date_to`, formatDate(to_date));
            }
          } else if (filterField === "created_at") {
            if (from_date) {
              params.append(`created_at_from`, formatDate(from_date));
            }
            if (to_date) {
              params.append(`created_at_to`, formatDate(to_date));
            }
          } else if (filterField === "updated_at") {
            if (from_date) {
              params.append(`updated_at_from`, formatDate(from_date));
            }
            if (to_date) {
              params.append(`updated_at_to`, formatDate(to_date));
            }
          }

          return;
        }

        // 🔤 Simple filters (string, number)
        params.append(filterField, String(filterValue));
      }
    );
  }

  try {
    const response = await Axios.get(
      `${api.warehouse2.pendingStockTransfer.list}?${params.toString()}`,
      {
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `pending_stock_transfer_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading CSV", error);
    throw error;
  }
};